// Period presets for ApprovedVsSettledPage. Kept out of the page so the URL
// parsing can be unit-tested without the router.

export type PeriodKey = 'this-month' | 'last-month' | 'this-quarter' | 'last-quarter' | 'ytd' | 'last-year' | 'last-12-months' | 'custom';

/** Resolve a period preset to (startMonth, endMonth) in YYYY-MM. */
export function resolvePeriod(period: PeriodKey, customStart: string, customEnd: string): { startMonth: string; endMonth: string; label: string } {
  const now = new Date();
  const ym = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  const startOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1);
  const addMonths = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth() + n, 1);
  const quarterStart = (d: Date) => new Date(d.getFullYear(), Math.floor(d.getMonth() / 3) * 3, 1);

  switch (period) {
    case 'this-month': {
      const m = ym(startOfMonth(now));
      return { startMonth: m, endMonth: m, label: 'This month' };
    }
    case 'last-month': {
      const m = ym(addMonths(startOfMonth(now), -1));
      return { startMonth: m, endMonth: m, label: 'Last month' };
    }
    case 'this-quarter': {
      const start = quarterStart(now);
      return { startMonth: ym(start), endMonth: ym(startOfMonth(now)), label: 'This quarter' };
    }
    case 'last-quarter': {
      const start = addMonths(quarterStart(now), -3);
      const end = addMonths(quarterStart(now), -1);
      return { startMonth: ym(start), endMonth: ym(end), label: 'Last quarter' };
    }
    case 'ytd': {
      return { startMonth: `${now.getFullYear()}-01`, endMonth: ym(startOfMonth(now)), label: 'Year to date' };
    }
    case 'last-year': {
      const y = now.getFullYear() - 1;
      return { startMonth: `${y}-01`, endMonth: `${y}-12`, label: `${y} (last year)` };
    }
    case 'last-12-months': {
      const start = ym(addMonths(startOfMonth(now), -11));
      return { startMonth: start, endMonth: ym(startOfMonth(now)), label: 'Last 12 months' };
    }
    case 'custom': {
      // start/end arrive from the URL; the callable only accepts YYYY-MM.
      const isMonth = (v: string) => /^\d{4}-\d{2}$/.test(v);
      const s = isMonth(customStart) ? customStart : `${now.getFullYear()}-01`;
      const e = isMonth(customEnd) ? customEnd : ym(startOfMonth(now));
      return { startMonth: s, endMonth: e, label: `${s} → ${e}` };
    }
    default: {
      // An unknown ?period= from a hand-edited link used to fall off the switch,
      // return undefined and crash the page on destructure.
      const start = ym(addMonths(startOfMonth(now), -11));
      return { startMonth: start, endMonth: ym(startOfMonth(now)), label: 'Last 12 months' };
    }
  }
}
